import { safeAssign, ErrorHandlerService } from 'hdi-components';
import {
  CaasPlatformData,
  CaasPlatformValue,
  CaasPodcastEpisodeData,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import {
  PodcastItems,
  PodcastPlatform,
  PodcastPlatforms,
} from '../../../hdi-components/src/shared/interfaces/podcast-item.interface';

// Type alias for our mapping configuration entry
type PodcastPropertyMapEntry = {
  key: keyof PodcastItems;
  getter: () => string | number;
  context: string;
};

/**
 * Helper for the innermost mapping (CaaS platform link to PodcastPlatform link).
 */
function mapCaasPlatformToPodcastPlatform(
  platform: CaasPlatformValue,
  errorHandler: ErrorHandlerService
): PodcastPlatform {
  let serieId = '';
  let alt = '';
  let label = '';
  let url = '';

  safeAssign(
    () => {
      serieId = platform.identifier;
    },
    '...',
    errorHandler
  );
  safeAssign(
    () => {
      alt = platform.formData?.lt_link_platform?.value?.label;
    },
    '...',
    errorHandler
  );
  safeAssign(
    () => {
      label = platform.formData?.lt_link_platform?.value?.label;
    },
    '...',
    errorHandler
  );
  safeAssign(
    () => {
      url = platform.formData?.lt_link_external?.value ?? '';
    },
    '...',
    errorHandler
  );

  return {
    alt: alt,
    label: label,
    url: url,
    serieId: serieId,
  };
}

/**
 * Helper to map a single CaaS platform serie to our PodcastPlatforms format.
 */
function mapSerieToLogoWall(
  podcastSerie: CaasPlatformData,
  errorHandler: ErrorHandlerService
): PodcastPlatforms {
  let serieId = '';
  let platformsList: PodcastPlatform[] = [];

  safeAssign(
    () => {
      serieId = podcastSerie.identifier;
    },
    'CaasContentService > mapSerieToLogoWall > serieId',
    errorHandler
  );

  safeAssign(
    () => {
      platformsList =
        podcastSerie?.formData?.tt_platforms?.value?.map(
          (platform: CaasPlatformValue) => {
            return mapCaasPlatformToPodcastPlatform(platform, errorHandler);
          }
        ) ?? [];
    },
    'CaasContentService > mapSerieToLogoWall > platforms',
    errorHandler
  );

  return {
    serieId: serieId,
    platforms: platformsList,
  };
}

/**
 * Helper to find the matching podcast serie for a given episode.
 * This contains the logic from the first nested block.
 */
function findPodcastSerie(
  episode: CaasPodcastEpisodeData,
  platforms: CaasPlatformData[],
  errorHandler: ErrorHandlerService
): CaasPlatformData | undefined {
  let podcastSerie: CaasPlatformData | undefined;
  safeAssign(
    () => {
      let podcastIdentifier: string | undefined;
      safeAssign(
        () => {
          podcastIdentifier = episode.formData.tt_podcast?.value?.identifier;
        },
        'CaasContentService > findPodcastSerie > podcastIdentifier',
        errorHandler
      );
      podcastSerie = platforms.find((p) => p.identifier === podcastIdentifier);
    },
    'CaasContentService > findPodcastSerie > podcastSerie',
    errorHandler
  );
  return podcastSerie;
}

/**
 * Returns the configuration map for mapping CaaS data to a PodcastItem.
 */
function getPodcastPropertyMap(
  episode: CaasPodcastEpisodeData,
  podcastSerie: CaasPlatformData | undefined
): PodcastPropertyMapEntry[] {
  return [
    {
      key: 'id',
      getter: () => episode.formData.tt_anchor_name?.value || '',
      context: 'id',
    },
    {
      key: 'subline',
      getter: () => episode.formData.tt_preheader?.value || '',
      context: 'subline',
    },
    {
      key: 'headline',
      getter: () => episode.formData.tt_title?.value || '',
      context: 'headline',
    },
    {
      key: 'content',
      getter: () => episode.formData.tt_bodytext?.value || '',
      context: 'content',
    },
    {
      key: 'image',
      getter: () => episode.formData.tt_image?.value?.url || '',
      context: 'image',
    },
    {
      key: 'imageIdentifier',
      getter: () => episode.formData.tt_image?.value?.identifier || '',
      context: 'imageIdentifier',
    },
    {
      key: 'imageUID',
      getter: () => episode.formData.tt_image?.value?.uid || '',
      context: 'imageUID',
    },
    {
      key: 'imageTitle',
      getter: () => episode.formData.tt_image_title?.value || '',
      context: 'imageTitle',
    },
    { key: 'key', getter: () => episode.identifier, context: 'key' },
    {
      key: 'url',
      getter: () => episode.formData?.tt_podigee_configuration?.value || '',
      context: 'url',
    },
    {
      key: 'title',
      getter: () => podcastSerie?.formData.tt_title?.value || '',
      context: 'title',
    },
    {
      key: 'timestamp',
      getter: () => new Date(episode.formData.tt_date?.value).getTime() || 0,
      context: 'timestamp',
    },
    {
      key: 'podcastSerieId',
      getter: () => episode.formData.tt_podcast?.value?.identifier || '',
      context: 'podcastSerieId',
    },
  ];
}

/**
 * Helper to build the logo wall list from all platforms.
 * This logic is now called only once.
 */
export function buildLogoWalls(
  platforms: CaasPlatformData[],
  errorHandler: ErrorHandlerService
): PodcastPlatforms[] {
  let logoWalls: PodcastPlatforms[] = [];
  safeAssign(
    () => {
      logoWalls = platforms
        .filter((platform) => {
          let hasPlatforms = false;
          safeAssign(
            () => {
              hasPlatforms =
                platform?.formData?.tt_platforms?.value?.length > 0;
            },
            'CaasContentService > buildLogoWalls > hasPlatforms',
            errorHandler
          );
          return hasPlatforms;
        })
        .map((podcastSerie) => {
          return mapSerieToLogoWall(podcastSerie, errorHandler);
        });
    },
    'CaasContentService > buildLogoWalls > logoWalls',
    errorHandler
  );
  return logoWalls;
}

/**
 * Main mapper function to convert a CaaS episode to a PodcastItem.
 */
export function mapEpisodeToPodcastItem(
  episode: CaasPodcastEpisodeData,
  platforms: CaasPlatformData[],
  errorHandler: ErrorHandlerService
): PodcastItems {
  // Helper call to find the matching serie
  const podcastSerie = findPodcastSerie(episode, platforms, errorHandler);

  if (!podcastSerie) {
    console.warn(`Podcast Serie not found for episode: ${episode.identifier}`);
  }

  // 1. Initialize the target object with default values.
  const podcastItem: PodcastItems = {
    id: '',
    subline: '',
    headline: '',
    content: '',
    image: '',
    imageIdentifier: '',
    imageUID: '',
    imageTitle: '',
    key: '',
    url: '',
    title: '',
    timestamp: 0,
    podcastSerieId: '',
  };

  const contextPrefix = 'CaasContentService > mapEpisodeToPodcastItem';

  // 2. Get the mapping configuration from the helper method
  const propertyMap = getPodcastPropertyMap(episode, podcastSerie);

  // 3. Execute all assignments safely using one loop.
  propertyMap.forEach((prop) => {
    safeAssign(
      () => {
        Object.assign(podcastItem, { [prop.key]: prop.getter() });
      },
      `${contextPrefix} > ${prop.context}`,
      errorHandler
    );
  });

  return podcastItem;
}
